import React, { useState, useEffect } from 'react';
import ccmService from '../../services/ccmService';
import './Assessments.css';

const ACUITY_OPTIONS = ['Low', 'Medium', 'High'];
const ACUITY_COLORS  = { Low: '#10b981', Medium: '#f59e0b', High: '#ef4444' };

const ASSESSMENT_DOMAINS = [
  {
    key: 'presenting_problem',
    label: 'Presenting Problem / Chief Complaint',
    placeholder: 'Describe the patient\'s main health concern or reason for this CCM encounter…',
    rows: 3,
  },
  {
    key: 'functional_status',
    label: 'Functional Status',
    hint: 'Activities of daily living, mobility, fall risk, cognitive function',
    placeholder: 'Describe the patient\'s current functional ability…',
    rows: 3,
  },
  {
    key: 'medication_review',
    label: 'Medication Review',
    hint: 'Adherence, side effects, barriers to taking medications',
    placeholder: 'List key medications reviewed and any adherence issues…',
    rows: 3,
  },
  {
    key: 'social_determinants',
    label: 'Social Determinants of Health',
    hint: 'Housing, transportation, food security, social support',
    placeholder: 'Document relevant social factors affecting the patient\'s health…',
    rows: 3,
  },
  {
    key: 'chronic_conditions_status',
    label: 'Chronic Conditions Status',
    hint: 'Current control of chronic conditions (Diabetes, HTN, CHF, etc.)',
    placeholder: 'Summarise the current status of each chronic condition…',
    rows: 4,
  },
  {
    key: 'patient_goals',
    label: 'Patient-Stated Goals & Preferences',
    placeholder: 'What does the patient want to achieve? Any preferences or concerns raised?',
    rows: 3,
  },
  {
    key: 'barriers_to_care',
    label: 'Barriers to Care',
    hint: 'Identify obstacles that may prevent the patient from meeting health goals',
    placeholder: 'Document identified barriers…',
    rows: 3,
  },
  {
    key: 'care_coordinator_notes',
    label: 'Care Coordinator Notes / Plan',
    placeholder: 'Summarise the clinical plan discussed with the patient during this call…',
    rows: 4,
  },
];

const emptyForm = () => ({
  acuity_level: 'Medium',
  ...Object.fromEntries(ASSESSMENT_DOMAINS.map(d => [d.key, ''])),
});

// ── History Card ──────────────────────────────────────────────────────────────
function AssessmentHistoryCard({ item }) {
  const [expanded, setExpanded] = useState(false);
  const ac = ACUITY_COLORS[item.acuity_level] || '#64748b';
  let responses = [];
  try {
    const raw = typeof item.responses === 'string' ? JSON.parse(item.responses) : item.responses;
    if (Array.isArray(raw)) responses = raw;
    else if (raw && typeof raw === 'object') responses = Object.entries(raw).map(([k, v]) => ({ key: k, value: v }));
  } catch { /* ignore */ }

  return (
    <div className="hra-hist-card">
      <div className="hra-hist-header" onClick={() => setExpanded(e => !e)}>
        <div className="hra-hist-left">
          <span className="hra-hist-date">
            {new Date(item.completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
          <span className="hra-hist-by">by {item.performed_by_name || 'Care Team'}</span>
        </div>
        <div className="hra-hist-right">
          <span className="hra-acuity-badge" style={{ background: `${ac}22`, color: ac, border: `1px solid ${ac}44` }}>
            {item.acuity_level} Risk
          </span>
          <span className="hra-chevron">{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {expanded && (
        <div className="hra-hist-body">
          {responses.length > 0 && responses.map((r, i) => {
            const domain = ASSESSMENT_DOMAINS.find(d => d.key === (r.key || r.questionId));
            const label = domain?.label || r.key || r.question || `Field ${i + 1}`;
            const value = r.value || r.answer || '';
            if (!value) return null;
            return (
              <div key={i} className="hra-hist-row">
                <p className="hra-hist-label">{label}</p>
                <p className="hra-hist-value">{value}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
const Assessments = ({
  enrollmentId,
  onWorkspaceRefresh,
}) => {
  const [loading, setLoading]       = useState(true);
  const [history, setHistory]       = useState([]);
  const [showForm, setShowForm]     = useState(false);
  const [form, setForm]             = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast]           = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    if (enrollmentId) fetchHistory();
    else setLoading(false);
  }, [enrollmentId]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const res = await ccmService.getPatientAssessments(enrollmentId);
      if (res.success) setHistory(res.data || []);
    } catch {
      showToast('Failed to load assessment history', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const hasContent = ASSESSMENT_DOMAINS.some(d => form[d.key]?.trim());
    if (!hasContent) { showToast('Please fill in at least one assessment field', 'error'); return; }

    setSubmitting(true);
    try {
      // Format as array of key-value pairs
      const responses = ASSESSMENT_DOMAINS
        .filter(d => form[d.key]?.trim())
        .map(d => ({ key: d.key, question: d.label, answer: form[d.key].trim() }));

      const res = await ccmService.submitAssessment({
        enrollment_id: enrollmentId,
        assessment_id: 1,          // generic ID — no template system
        acuity_level: form.acuity_level,
        responses,
      });

      if (res.success) {
        showToast(`Assessment saved — Risk Level: ${res.data?.acuity_level || form.acuity_level}`);
        setShowForm(false);
        setForm(emptyForm());
        fetchHistory();
        onWorkspaceRefresh?.();
      } else {
        showToast(res.message || 'Failed to submit', 'error');
      }
    } catch {
      showToast('An error occurred while submitting', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const acuityColor = ACUITY_COLORS[form.acuity_level] || '#64748b';
  return (
    <div className="hra-root">
      {toast && (
        <div className={`hra-toast hra-toast--${toast.type}`}>{toast.msg}</div>
      )}

      {loading ? (
        <div className="hra-loading">
          <div className="hra-spinner" /> Loading…
        </div>
      ) : history.length === 0 ? (
        <div className="cpws-tab-empty">
          <h4>No Assessments on Record</h4>
          <p>Document the first health risk assessment for this patient&apos;s CCM encounter.</p>
          {!showForm && (
            <button type="button" className="cp-primary-btn cpws-tab-empty-btn" onClick={() => setShowForm(true)}>
              New Assessment
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="hra-history">
            {history.map((item) => (
              <AssessmentHistoryCard key={item.id} item={item} />
            ))}
          </div>
          {!showForm && (
            <button type="button" className="cpws-add-link" onClick={() => setShowForm(true)}>
              + New Assessment
            </button>
          )}
        </>
      )}

      {showForm && (
        <div className="hra-modal-overlay">
          <div className="hra-modal-content">
            {/* ══════════ ASSESSMENT FORM ══════════ */}
            <form onSubmit={handleSubmit} className="hra-form" style={{ boxShadow: 'none' }}>
              <div className="hra-form-header">
                <h4 className="hra-form-title">New Clinical Assessment</h4>
                <button type="button" className="hra-cancel-btn" onClick={() => setShowForm(false)}>Cancel</button>
              </div>

              {/* Acuity / Risk Level */}
              <div className="hra-acuity-row">
                <label className="hra-label">Overall Risk / Acuity Level</label>
                <div className="hra-acuity-options">
                  {ACUITY_OPTIONS.map(opt => (
                    <label
                      key={opt}
                      className={`hra-acuity-option ${form.acuity_level === opt ? 'selected' : ''}`}
                      style={form.acuity_level === opt ? { background: `${ACUITY_COLORS[opt]}18`, borderColor: ACUITY_COLORS[opt], color: ACUITY_COLORS[opt] } : {}}
                    >
                      <input
                        type="radio"
                        name="acuity"
                        value={opt}
                        checked={form.acuity_level === opt}
                        onChange={() => setForm(f => ({ ...f, acuity_level: opt }))}
                        style={{ display: 'none' }}
                      />
                      <span
                        className="hra-acuity-dot"
                        style={{ background: ACUITY_COLORS[opt] }}
                      />
                      {opt} Risk
                    </label>
                  ))}
                </div>
              </div>

              <div className="hra-divider" />

              {/* Domain Fields */}
              <div className="hra-domains">
                {ASSESSMENT_DOMAINS.map(domain => (
                  <div key={domain.key} className="hra-domain-group">
                    <label className="hra-label">
                      {domain.label}
                      {domain.hint && <span className="hra-label-hint"> — {domain.hint}</span>}
                    </label>
                    <textarea
                      className="hra-textarea"
                      rows={domain.rows || 3}
                      placeholder={domain.placeholder}
                      value={form[domain.key] || ''}
                      onChange={(e) => setForm(f => ({ ...f, [domain.key]: e.target.value }))}
                    />
                  </div>
                ))}
              </div>

              <div className="hra-form-footer">
                <button type="button" className="hra-cancel-btn" onClick={() => setShowForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="hra-primary-btn" disabled={submitting}>
                  {submitting ? 'Saving…' : 'Sign & Submit Assessment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Assessments;
